/**
 * 一键归集明细 — historical one-click consolidation credits on master account.
 * URL: /imputation
 *
 * GET /api/users/imputation/logs
 */
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { usersAPI } from '../api/users';
import { EmptyState } from '../components/ui/EmptyState';
import { LoadingSkeleton } from '../components/ui/LoadingSkeleton';
import { QueryErrorNote } from '../components/ui/QueryErrorNote';
import { ThemedPageHeader } from '../components/ui/ThemedPageHeader';
import { THEMED_SURFACE_COLORS as C, ThemedPanel } from '../components/ui/ThemedSurface';

const PAGE_SIZE = 20;

const ASSET_COLORS: Record<string, string> = {
  EP: C.text,
  SP1: '#5eead4',
  SP2: '#86efac',
  SP3: '#c4b5fd',
  RP: C.text,
  MP: C.text,
  FP: C.text,
};

function fmtTime(value: string): string {
  const trimmed = value.trim();
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(trimmed)) return trimmed;
  const date = new Date(trimmed.endsWith('Z') ? trimmed : `${trimmed.replace(' ', 'T')}Z`);
  if (Number.isNaN(date.getTime())) return trimmed;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

function fmtAmount(value: string): string {
  const n = Number(value);
  if (!Number.isFinite(n)) return value;
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function MetricRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between py-1.5 text-sm">
      <span style={{ color: ASSET_COLORS[label] ?? C.muted }}>{label}</span>
      <span className="font-medium tabular-nums" style={{ color: ASSET_COLORS[label] ?? C.text }}>
        {fmtAmount(value)}
      </span>
    </div>
  );
}

export function ImputationDetailsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ['users', 'imputation', 'logs'],
    queryFn: () => usersAPI.getImputationLogs({ page: 1, limit: PAGE_SIZE }),
    staleTime: 60_000,
    gcTime: 5 * 60_000,
  });

  const rows = data?.rows ?? [];

  const metrics = useMemo(
    () =>
      [
        { key: 'ep', label: 'EP' },
        { key: 'sp1', label: 'SP1' },
        { key: 'sp3', label: 'SP3' },
        { key: 'rp', label: 'RP' },
        { key: 'mp', label: 'MP' },
        { key: 'fp', label: 'FP' },
      ] as const,
    [],
  );

  const headerTheme = {
    topbarBg: '#131f2f',
    titleGradient: C.titleGradient,
    text: C.text,
  };

  return (
    <div className="min-h-full" style={{ background: '#131f2f' }}>
      <ThemedPageHeader
        title={t('me_finance.imputation', '一键归集明细')}
        onBack={() => navigate(-1)}
        theme={headerTheme}
      />

      <div className={`space-y-3 px-4 pb-8 pt-[68px]${isFetching && data ? ' opacity-80' : ''}`}>
        {isError && (
          <QueryErrorNote
            message={t('imputationDetails.loadFailed', '加载失败')}
            onRetry={() => void refetch()}
          />
        )}

        {isLoading && !data ? (
          <div className="space-y-3">
            <LoadingSkeleton className="h-44" />
            <LoadingSkeleton className="h-44" />
          </div>
        ) : rows.length === 0 ? (
          <EmptyState title={t('common.noData', '暂无数据')} className="py-16" />
        ) : (
          rows.map((row) => (
            <ThemedPanel key={row.id} className="px-4 py-3" style={{ background: '#222d3f' }}>
              <div className="mb-2 flex items-center justify-between border-b pb-2 text-sm" style={{ borderColor: '#131f2f' }}>
                <span className="text-white/70">{t('imputationDetails.time', '时间')}</span>
                <span className="font-medium text-white">{fmtTime(row.create_time)}</span>
              </div>
              {metrics.map(({ key, label }) => (
                <MetricRow key={key} label={label} value={row[key]} />
              ))}
            </ThemedPanel>
          ))
        )}
      </div>
    </div>
  );
}

export default ImputationDetailsPage;
